import { Deferred } from '@inertiajs/react'
import { router } from '@inertiajs/react'
import { ArrowRight } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/Card'
import { KpiRow } from '@/components/analytics/KpiRow'
import { ConversationVolumeCard } from '@/components/analytics/ConversationVolumeCard'
import { FirstResponseTimeCard } from '@/components/analytics/FirstResponseTimeCard'
import { AiSuggestionsCard } from '@/components/analytics/AiSuggestionsCard'
import { QuickWinsCard } from '@/components/analytics/QuickWinsCard'
import { PanelSkeleton } from '@/components/analytics/PanelSkeleton'
import { usePanel } from '@/components/analytics/usePanel'
import routes from '@/routeHelpers'
import { useRouteParams } from '@/hooks/useRoutes'
import type {
  AnalyticsFilters,
  KpisPanel,
  ConversationVolumePanel,
  FirstResponseTimePanel,
  AISuggestionsPanel,
  QuickWinsPanel,
} from '@/types/analytics'

interface OverviewSectionProps {
  filters: AnalyticsFilters
}

const DEEP_LINKS: { section: AnalyticsFilters['section']; label: string; description: string }[] = [
  { section: 'conversations', label: 'Conversations', description: 'Volume, channel mix, where chats come from' },
  { section: 'response_time', label: 'Response time', description: 'First-reply distribution by SLA and country' },
  { section: 'agents', label: 'Agents', description: 'Per-agent volume and median reply time' },
  { section: 'topics', label: 'Topics', description: 'What customers are asking about' },
]

export function OverviewSection({ filters }: OverviewSectionProps) {
  const { withAccount } = useRouteParams()

  const goTo = (section: AnalyticsFilters['section']) => {
    router.visit(routes.analytics.show.path(withAccount()), {
      data: { section, range: filters.range, inbox_id: filters.inbox_id ?? '' },
      preserveScroll: true,
      preserveState: true,
    })
  }

  return (
    <>
      <Deferred data="kpis" fallback={<PanelSkeleton className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" count={6} />}>
        <KpisDeferred rangeLabel={filters.range_label} />
      </Deferred>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2">
          <Deferred data="conversation_volume" fallback={<PanelSkeleton className="h-80" />}>
            <ConversationVolumeDeferred />
          </Deferred>
        </div>
        <Deferred data="first_response_time" fallback={<PanelSkeleton className="h-80" />}>
          <FirstResponseTimeDeferred />
        </Deferred>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Deferred data="ai_suggestions" fallback={<PanelSkeleton className="h-48" />}>
          <AiSuggestionsDeferred />
        </Deferred>
        <Deferred data="quick_wins" fallback={<PanelSkeleton className="h-48" />}>
          <QuickWinsDeferred />
        </Deferred>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {DEEP_LINKS.map((link) => (
          <button
            key={link.section}
            type="button"
            onClick={() => goTo(link.section)}
            className="text-left cursor-pointer"
          >
            <Card className="p-4 h-full hover:border-primary/50 transition-colors">
              <CardContent className="px-0 py-0 flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium text-sm">{link.label}</div>
                  <p className="text-xs text-muted-foreground mt-1">{link.description}</p>
                </div>
                <ArrowRight className="size-4 text-muted-foreground shrink-0 mt-0.5" />
              </CardContent>
            </Card>
          </button>
        ))}
      </div>
    </>
  )
}

function KpisDeferred({ rangeLabel }: { rangeLabel: string }) {
  const data = usePanel<KpisPanel>('kpis')
  return data ? <KpiRow data={data} rangeLabel={rangeLabel} /> : null
}

function ConversationVolumeDeferred() {
  const data = usePanel<ConversationVolumePanel>('conversation_volume')
  return data ? <ConversationVolumeCard data={data} /> : null
}

function FirstResponseTimeDeferred() {
  const data = usePanel<FirstResponseTimePanel>('first_response_time')
  return data ? <FirstResponseTimeCard data={data} mode="absolute" /> : null
}

function AiSuggestionsDeferred() {
  const data = usePanel<AISuggestionsPanel>('ai_suggestions')
  return data ? <AiSuggestionsCard data={data} /> : null
}

function QuickWinsDeferred() {
  const data = usePanel<QuickWinsPanel>('quick_wins')
  return data ? <QuickWinsCard data={data} /> : null
}
